/** Size charts progressively enhance the complete server-rendered CM tables. */
export function inchesFromCm(value) {
  const text = String(value).trim();
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) return null;
  const number = Number(text);
  if (!Number.isFinite(number) || number <= 0) return null;
  return String(Math.round((number / 2.54) * 10) / 10);
}

class SizeCharts extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.panels = [...this.querySelectorAll('[data-size-panel]')];
    this.navigation = this.querySelector('[data-size-tabs]');
    this.units = this.querySelector('[data-size-units]');
    this.note = this.querySelector('[data-size-note]');
    this.unit = 'cm';
    if (!this.navigation || !this.units) return;
    this.navigation.replaceChildren();
    this.navigation.setAttribute('role', 'tablist');
    this.navigation.setAttribute('aria-label', this.querySelector('.bm-size__eyebrow')?.textContent || '');
    this.tabs = this.panels.map((panel, index) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'bm-size__tab';
      tab.id = `${panel.id}-tab`;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.append(panel.querySelector('[data-size-tab]').content.cloneNode(true));
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      tab.addEventListener('click', () => this.select(index), { signal });
      tab.addEventListener('keydown', event => {
        let target;
        if (event.key === 'ArrowRight') target = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') target = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') target = 0;
        if (event.key === 'End') target = this.tabs.length - 1;
        if (target === undefined) return;
        event.preventDefault();
        this.select(target);
        this.tabs[target].focus();
      }, { signal });
      this.navigation.append(tab);
      return tab;
    });
    this.navigation.hidden = this.panels.length === 0;
    this.units.hidden = this.panels.length === 0;
    this.units.querySelectorAll('[data-unit]').forEach(button => button.addEventListener('click', () => {
      this.setUnit(button.dataset.unit);
    }, { signal }));
    this.addEventListener('shopify:block:select', event => {
      const panel = event.target.closest('[data-size-panel]');
      const index = this.panels.indexOf(panel);
      if (index >= 0) this.select(index);
    }, { signal });
    const revealHash = () => {
      const index = this.panels.findIndex(panel => `#${panel.id}` === window.location.hash);
      if (index >= 0) this.select(index);
    };
    window.addEventListener('hashchange', revealHash, { signal });
    this.select(0);
    this.setUnit('cm');
    revealHash();
  }

  select(index) {
    this.panels[index]?.querySelector('.bm-size__chart-heading')?.append(this.units);
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
      this.tabs[i].setAttribute('aria-selected', String(i === index));
      this.tabs[i].tabIndex = i === index ? 0 : -1;
    });
  }

  setUnit(unit) {
    this.unit = unit === 'in' ? 'in' : 'cm';
    this.querySelectorAll('[data-cm]').forEach(cell => {
      cell.textContent = this.unit === 'cm' ? cell.dataset.cm : (inchesFromCm(cell.dataset.cm) ?? '—');
    });
    this.units.querySelectorAll('[data-unit]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.unit === this.unit)));
    this.note.textContent = this.unit === 'cm' ? this.dataset.cmNote : this.dataset.inNote;
  }

  disconnectedCallback() {
    this.abort?.abort();
  }
}

class SizeAnchor extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.update = () => {
      const target = document.querySelector('[data-size-measure]');
      const link = this.querySelector('a');
      if (!link) return;
      link.hidden = !target;
      if (target) link.setAttribute('href', `#${target.id}`);
    };
    this.update();
    document.addEventListener('shopify:section:load', this.update, { signal: this.abort.signal });
    document.addEventListener('shopify:section:unload', () => {
      this.frame = requestAnimationFrame(this.update);
    }, { signal: this.abort.signal });
    document.addEventListener('shopify:section:reorder', this.update, { signal: this.abort.signal });
    this.addEventListener('click', event => {
      const link = event.target.closest('a');
      const target = document.querySelector('[data-size-measure]');
      if (!link || !target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      target.focus({ preventScroll: true });
    }, { signal: this.abort.signal });
  }
  disconnectedCallback() {
    this.abort?.abort();
    cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('bm-size-charts')) customElements.define('bm-size-charts', SizeCharts);
if (!customElements.get('bm-size-anchor')) customElements.define('bm-size-anchor', SizeAnchor);
